import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import CountryPhoneInput from "@/components/CountryPhoneInput";
import { useAuth, EXPIRED_FLAG } from "@/context/AuthContext";

const homeFor = (role) => (role === "field_officer" ? "/field" : role === "station_staff" ? "/station" : "/dashboard");

export default function Login() {
  const { user, login, formatDetail } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expired] = useState(() => sessionStorage.getItem(EXPIRED_FLAG) === "1");

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(phone, password);
      nav(homeFor(u?.role));
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-slate-50" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBtZXNoJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MzMwMTMzNnww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900/90 via-brand-800/70 to-slate-900/90" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-flame-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <img
            src="/gasify_logo.png"
            alt="Gasify"
            className="h-28 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
            data-testid="login-brand-logo"
          />
        </div>
        <div className="relative max-w-xl">
          <div className="text-sm uppercase tracking-[0.25em] text-flame-400 font-semibold mb-3" data-testid="login-welcome">
            Welcome to Gasify Console
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
            Clean cooking powered by <span className="text-flame-400">Pay-As-You-Cook</span> LPG.
          </h1>
          <p className="mt-4 text-slate-300 text-sm max-w-md leading-relaxed">
            Monitor customers, smart meters, LPG consumption, and daily operations.
          </p>
        </div>
        <div className="relative" />
      </div>

      {/* Right: form */}
      <div className="lg:col-span-2 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden mb-6 flex justify-center">
            <img
              src="/gasify_logo.png"
              alt="Gasify"
              className="h-16 w-auto"
              data-testid="login-brand-logo-mobile"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-flame-600 mb-2 font-semibold">Sign in</div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1">Enter the phone number and password associated with your account.</p>
          </div>
          {expired && !error && (
            <div className="border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 rounded-sm" data-testid="login-session-expired">
              Your session expired for security. Please sign in again.
            </div>
          )}

          {error && (
            <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Phone number</Label>
              <div className="mt-1">
                <CountryPhoneInput
                  value={phone}
                  onChange={setPhone}
                  autoFocus
                  testId="login-phone"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Password</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1 rounded-sm"
              />
            </div>
          </div>

          <Button
            type="submit" disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-brand-600 hover:bg-brand-700 rounded-sm h-10 font-semibold"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
